import { useState, useEffect } from "react";
import GeneralExpensesList from "./GeneralExpensesList";
import { toast } from "sonner";
import { Users, Calendar, DollarSign, Plus, Trash2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { PieSliceDetails } from "./PieSliceDetails";
import { MembershipValueCard } from "./MembershipValueCard";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from "recharts";

const COLORS = ["#1e3a5f", "#2d8a4e", "#f59e0b", "#8b5cf6", "#ef4444", "#06b6d4", "#ec4899", "#84cc16", "#f97316", "#14b8a6"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function FAAnalytics() {
  const [tab, setTab] = useState("people");
  const tabs = [
    { id: "people", label: "People", icon: Users },
    { id: "events", label: "Events", icon: Calendar },
    { id: "finance", label: "Finance", icon: DollarSign },
  ];
  return (
    <div data-testid="fa-analytics">
      <div className="flex gap-2 mb-4 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap flex items-center gap-1.5 ${tab === t.id ? "bg-[#1e3a5f] text-white" : "bg-white text-slate-600 border"}`} data-testid={`analytics-${t.id}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>
      {tab === "people" && <PeopleTab />}
      {tab === "events" && <EventsTab />}
      {tab === "finance" && <FinanceTab />}
    </div>
  );
}

function KPI({ label, value, sub, color = "text-[#1e3a5f]" }) {
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm text-center">
      <p className={`text-2xl font-extrabold ${color}`}>{value}</p>
      <p className="text-slate-500 text-xs">{label}</p>
      {sub && <p className="text-slate-400 text-[10px] mt-0.5">{sub}</p>}
    </div>
  );
}

function Card({ title, children, className = "" }) {
  return <div className={`bg-white rounded-2xl p-4 shadow-sm ${className}`}><h4 className="text-xs font-bold text-slate-500 mb-2">{title}</h4>{children}</div>;
}

// ============ PEOPLE TAB (original layout) ============
function PeopleTab() {
  const [data, setData] = useState(null);
  const { authAxios } = useAuth();
  useEffect(() => { authAxios.get("/fa-analytics/people").then(r => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="text-center py-8 text-slate-400">Loading...</div>;
  const quarterData = Object.entries(data.new_per_quarter).map(([k, v]) => ({ name: k, count: v })).slice(-4);
  const guestQuarterData = Object.entries(data.new_guests_per_quarter).map(([k, v]) => ({ name: k, count: v })).slice(-4);
  return (
    <div className="space-y-4">
      <h3 className="font-bold text-sm text-[#1e3a5f]">Members</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KPI label="Active Members" value={data.active_members} />
        <KPI label="Engaged %" value={`${data.engaged_pct}%`} color="text-green-600" sub="Last 3 months" />
        <KPI label="Growth YTD" value={`${data.growth_ytd > 0 ? "+" : ""}${data.growth_ytd}%`} color={data.growth_ytd >= 0 ? "text-green-600" : "text-red-500"} />
        <KPI label="Churn YTD" value={`${data.churn_ytd}%`} color={data.churn_ytd > 5 ? "text-red-500" : "text-amber-600"} />
      </div>
      <MembershipValueCard />
      {quarterData.length > 0 && (
        <Card title="New Members per Quarter">
          <ResponsiveContainer width="100%" height={150}>
            <BarChart data={quarterData}><XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip /><Bar dataKey="count" fill="#1e3a5f" radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </Card>
      )}
      <h3 className="font-bold text-sm text-[#1e3a5f] mt-4">Guests</h3>
      <div className="grid grid-cols-3 gap-3">
        <KPI label="Active Guests" value={data.active_guests} color="text-[#2d8a4e]" />
        <KPI label="Engaged %" value={`${data.guest_engaged_pct}%`} color="text-green-600" sub="Last 3 months" />
        <KPI label="New (Latest Q)" value={Object.values(data.new_guests_per_quarter).slice(-1)[0] || 0} color="text-[#2d8a4e]" />
      </div>
    </div>
  );
}

// ============ EVENTS TAB (rearranged) ============
function EventsTab() {
  const [data, setData] = useState(null);
  const [customMode, setCustomMode] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const { authAxios } = useAuth();
  useEffect(() => {
    let url = "/fa-analytics/events";
    if (customMode && startDate && endDate) {
      url += `?start_date=${startDate}&end_date=${endDate}`;
    }
    authAxios.get(url).then(r => setData(r.data)).catch(() => {});
  }, [customMode, startDate, endDate]);
  if (!data) return <div className="text-center py-8 text-slate-400">Loading...</div>;
  const monthlyData = MONTHS.map((m, i) => ({ name: m, current: data.monthly_current[i], prev: data.monthly_prev[i] }));
  const rangeLabel = data.current_label || String(data.current_year);
  const prevLabel = data.prev_label || String(data.current_year - 1);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center bg-white rounded-xl px-3 py-2 shadow-sm">
        <button
          onClick={() => { setCustomMode(false); setStartDate(""); setEndDate(""); }}
          className={`px-3 py-1.5 rounded-full text-xs font-medium ${!customMode ? "bg-[#1e3a5f] text-white" : "bg-slate-100 text-slate-600"}`}
          data-testid="fa-events-preset-ytd"
        >Year to date</button>
        <button
          onClick={() => setCustomMode(v => !v)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium ${customMode ? "bg-[#1e3a5f] text-white" : "bg-slate-100 text-slate-600"}`}
          data-testid="fa-events-custom-toggle"
        >Custom range</button>
        {customMode && (
          <div className="flex items-center gap-2 text-xs">
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="px-2 py-1.5 rounded-lg border border-slate-200" data-testid="fa-events-start-date" />
            <span className="text-slate-400">→</span>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="px-2 py-1.5 rounded-lg border border-slate-200" data-testid="fa-events-end-date" />
          </div>
        )}
        <span className="ml-auto text-[10px] text-slate-400">Compared to {prevLabel}</span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <KPI label={`Events in ${rangeLabel}`} value={data.events_ytd} />
        <KPI label="Profitability" value={`${data.profitability_ytd > 0 ? "+" : ""}${data.profitability_ytd}%`} color={data.profitability_ytd >= 0 ? "text-green-600" : "text-red-500"} />
        <KPI label="Avg Attendance" value={data.avg_attendance_by_year.length > 0 ? data.avg_attendance_by_year.slice(-1)[0].avg : "\u2014"} />
      </div>

      {/* Row 1: Events YoY */}
      <Card title={`Events: ${rangeLabel} vs ${prevLabel}`}>
        <ResponsiveContainer width="100%" height={150}>
          <BarChart data={monthlyData}><XAxis dataKey="name" tick={{ fontSize: 9 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip /><Bar dataKey="current" fill="#1e3a5f" name={rangeLabel} radius={[3, 3, 0, 0]} /><Bar dataKey="prev" fill="#cbd5e1" name={prevLabel} radius={[3, 3, 0, 0]} /></BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Row 2: Revenue & Cost | Sales per Merchant — iter195: lone card
          promotes to sm:col-span-2 so it fills the row width when the
          right-side card has no data (previously left half-empty). */}
      {(() => {
        const showRevCost = data.event_profitability.length > 0;
        const showMerchants = data.sales_by_merchant.length > 0;
        if (!showRevCost && !showMerchants) return null;
        const soloClass = (showRevCost && showMerchants) ? "" : "sm:col-span-2";
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="fa-events-row-revcost">
            {showRevCost && (
              <div className={soloClass}>
                <Card title={`Revenue & Cost (${rangeLabel})`}>
                  <ResponsiveContainer width="100%" height={Math.max(120, data.event_profitability.length * 40)}>
                    <BarChart data={data.event_profitability} layout="vertical"><XAxis type="number" tick={{ fontSize: 9 }} /><YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={65} /><Tooltip /><Legend wrapperStyle={{ fontSize: 9 }} /><Bar dataKey="revenue" fill="#2d8a4e" name="Revenue" radius={[0, 3, 3, 0]} /><Bar dataKey="cost" fill="#ef4444" name="Cost" radius={[0, 3, 3, 0]} /></BarChart>
                  </ResponsiveContainer>
                </Card>
              </div>
            )}
            {showMerchants && (
              <div className={soloClass}>
                <Card title={`Sales per Merchant (${rangeLabel})`}>
                  <ResponsiveContainer width="100%" height={Math.max(120, data.sales_by_merchant.length * 35)}>
                    <BarChart data={data.sales_by_merchant} layout="vertical"><XAxis type="number" tick={{ fontSize: 9 }} /><YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={65} /><Tooltip /><Bar dataKey="value" fill="#2d8a4e" radius={[0, 3, 3, 0]} /></BarChart>
                  </ResponsiveContainer>
                </Card>
              </div>
            )}
          </div>
        );
      })()}

      {/* Row 3: Profitability % | Reach */}
      {(() => {
        const showProfPct = data.event_profitability.length > 0;
        const showReach = data.event_profitability.some(ep => ep.reach > 0);
        if (!showProfPct && !showReach) return null;
        const soloClass = (showProfPct && showReach) ? "" : "sm:col-span-2";
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="fa-events-row-profpct">
            {showProfPct && (
              <div className={soloClass}>
                <Card title={`Profitability % (${rangeLabel})`}>
                  <ResponsiveContainer width="100%" height={Math.max(120, data.event_profitability.length * 35)}>
                    <BarChart data={data.event_profitability} layout="vertical"><XAxis type="number" tick={{ fontSize: 9 }} unit="%" /><YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={65} /><Tooltip formatter={(v) => `${v}%`} /><Bar dataKey="profit_pct" name="%" radius={[0, 3, 3, 0]}>{data.event_profitability.map((ep, i) => <Cell key={i} fill={ep.profit_pct >= 0 ? "#2d8a4e" : "#ef4444"} />)}</Bar></BarChart>
                  </ResponsiveContainer>
                </Card>
              </div>
            )}
            {showReach && (
              <div className={soloClass}>
                <Card title="Reach (Footfall) per Event">
                  <ResponsiveContainer width="100%" height={Math.max(120, data.event_profitability.length * 35)}>
                    <BarChart data={data.event_profitability} layout="vertical"><XAxis type="number" tick={{ fontSize: 9 }} /><YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={65} /><Tooltip /><Bar dataKey="reach" fill="#8b5cf6" radius={[0, 3, 3, 0]} /></BarChart>
                  </ResponsiveContainer>
                </Card>
              </div>
            )}
          </div>
        );
      })()}

      {/* Row 4: Profitability Over Years | Avg Attendance Over Years */}
      {(() => {
        const showProfYears = data.profitability_by_year.length > 0;
        const showAvgAtt = data.avg_attendance_by_year.length > 0;
        if (!showProfYears && !showAvgAtt) return null;
        const soloClass = (showProfYears && showAvgAtt) ? "" : "sm:col-span-2";
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="fa-events-row-profyears">
            {showProfYears && (
              <div className={soloClass}>
                <Card title="Profitability % Over Years">
                  <ResponsiveContainer width="100%" height={130}>
                    <LineChart data={data.profitability_by_year}><XAxis dataKey="year" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} unit="%" /><Tooltip formatter={(v) => `${v}%`} /><Line type="monotone" dataKey="pct" stroke="#1e3a5f" strokeWidth={2} dot={{ r: 4 }} /></LineChart>
                  </ResponsiveContainer>
                </Card>
              </div>
            )}
            {showAvgAtt && (
              <div className={soloClass}>
                <Card title="Avg Attendance Over Years">
                  <ResponsiveContainer width="100%" height={130}>
                    <LineChart data={data.avg_attendance_by_year}><XAxis dataKey="year" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip /><Line type="monotone" dataKey="avg" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 4 }} /></LineChart>
                  </ResponsiveContainer>
                </Card>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

// ============ FINANCE TAB ============
function FinanceTab() {
  const [fin, setFin] = useState(null);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ description: "", amount: "", category: "", type: "revenue", date: "" });
  const { authAxios } = useAuth();
  useEffect(() => { fetchAll(); }, []);
  const fetchAll = async () => {
    try {
      const [finRes, itemsRes, catRes] = await Promise.all([authAxios.get("/fa-analytics/finance"), authAxios.get("/org-finance"), authAxios.get("/categories")]);
      setFin(finRes.data); setItems(itemsRes.data); setCategories(catRes.data);
    } catch (err) { console.error("Request failed:", err?.message || err); }
  };
  const handleAdd = async () => {
    if (!form.description || !form.amount) return toast.error("Description and amount required");
    try { await authAxios.post("/org-finance", form); toast.success("Added"); setForm({ description: "", amount: "", category: "", type: "revenue", date: "" }); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const handleDelete = async (id) => { try { await authAxios.delete(`/org-finance/${id}`); fetchAll(); } catch (err) { console.error("Request failed:", err?.message || err); } };
  if (!fin) return <div className="text-center py-8 text-slate-400">Loading...</div>;
  const filteredCats = categories.filter(c => c.type === form.type);
  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <KPI label="Total Revenue" value={`\u20ac${fin.total_revenue.toFixed(2)}`} color="text-green-600" />
        <KPI label="Total Cost" value={`\u20ac${fin.total_costs.toFixed(2)}`} color="text-red-500" />
        <KPI label="Unused Wallet" value={`\u20ac${fin.unused_wallet.toFixed(2)}`} color="text-amber-600" />
        <KPI label="ARPM" value={`\u20ac${fin.arpm.toFixed(2)}`} sub="Revenue / Member" />
        <KPI label="Cost / Member" value={`\u20ac${fin.cost_per_member.toFixed(2)}`} color="text-red-500" sub="OpCost / Member" />
      </div>

      {/* Pies row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {fin.revenue_by_source.length > 0 && <PieCard title="Revenue by Source" data={fin.revenue_by_source} byYear={fin.pies_by_year?.revenue} offset={0} pie="revenue" testid="pie-revenue" />}
        {fin.cost_by_category && fin.cost_by_category.length > 0 && (
          <PieCard title="Cost by Category" showTotal data={fin.cost_by_category} byYear={fin.pies_by_year?.cost} offset={3} pie="cost" testid="pie-cost" />
        )}
        {fin.spent_by_type.length > 0 && <PieCard title="Payments by Type" data={fin.spent_by_type} byYear={fin.pies_by_year?.payments} offset={5} pie="payments" testid="pie-payments" />}
      </div>

      {/* Trends row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {fin.arpm_trend.length > 0 && (
          <Card title="ARPM Trend (Revenue per Member)">
            <ResponsiveContainer width="100%" height={130}>
              <LineChart data={fin.arpm_trend}><XAxis dataKey="year" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip /><Line type="monotone" dataKey="arpm" stroke="#1e3a5f" strokeWidth={2} dot={{ r: 4 }} name="ARPM (\u20ac)" /></LineChart>
            </ResponsiveContainer>
          </Card>
        )}
        {fin.cost_trend.length > 0 && (
          <Card title="Operating Cost per Member Trend">
            <ResponsiveContainer width="100%" height={130}>
              <LineChart data={fin.cost_trend}><XAxis dataKey="year" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip /><Line type="monotone" dataKey="cost" stroke="#ef4444" strokeWidth={2} dot={{ r: 4 }} name="Cost (\u20ac)" /></LineChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>

      {fin.on_attend_credits && fin.on_attend_credits.totals.total_issued > 0 && (
        <OnAttendCreditsCard data={fin.on_attend_credits} />
      )}

      {/* Non-event lines */}
      <Card title="Non-Event Revenue & Costs">
        <div className="space-y-2 mb-3">
          <div className="flex gap-2">
            <select value={form.type} onChange={e => setForm({...form, type: e.target.value, category: ""})} className="px-3 py-2 rounded-xl border text-sm">
              <option value="revenue">Revenue</option><option value="expense">Expense</option>
            </select>
            <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="flex-1 px-3 py-2 rounded-xl border text-sm">
              <option value="">Category</option>{filteredCats.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <div className="flex flex-wrap gap-2">
            <input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Description *" className="flex-1 basis-full sm:basis-auto min-w-0 px-3 py-2 rounded-xl border text-sm" />
            <input type="number" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} placeholder="Amount *" className="w-28 flex-1 sm:flex-none min-w-0 px-3 py-2 rounded-xl border text-sm" />
            <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="w-36 flex-1 sm:flex-none min-w-0 px-3 py-2 rounded-xl border text-sm" />
            <button onClick={handleAdd} className="px-4 py-2 rounded-xl bg-[#1e3a5f] text-white font-bold"><Plus className="w-4 h-4" /></button>
          </div>
        </div>
        {items.map(i => (
          <div key={i.id} className={`flex items-center justify-between rounded-lg px-3 py-1.5 text-sm mb-1 ${i.type === "revenue" ? "bg-green-50" : "bg-red-50"}`}>
            <div><span className="font-medium">{i.description}</span> <span className="text-xs text-slate-400">{i.category} | {i.date}</span></div>
            <div className="flex items-center gap-2">
              <span className={`font-bold ${i.type === "revenue" ? "text-green-600" : "text-red-500"}`}>{i.type === "revenue" ? "+" : "-"}{"\u20ac"}{i.amount.toFixed(2)}</span>
              <button onClick={() => handleDelete(i.id)} className="text-red-400"><Trash2 className="w-3 h-3" /></button>
            </div>
          </div>
        ))}
        <GeneralExpensesList />
      </Card>
    </div>
  );
}

// Feature 2A/2B — visual summary of on-attend credits issued (wallet + coupons).
// Coupon "remaining" = balance still redeemable at merchants; "redeemed" = spent.
function OnAttendCreditsCard({ data }) {
  const t = data.totals || {};
  return (
    <Card title="On-Attend Credits">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <MiniStat label="Total issued" value={`\u20ac${t.total_issued.toFixed(2)}`} />
        <MiniStat label="Wallet credits" value={`\u20ac${t.wallet_amount.toFixed(2)}`} sub={`${t.wallet_count} member/guest`} />
        <MiniStat label="Coupons (unregistered)" value={`\u20ac${t.coupon_amount.toFixed(2)}`} sub={`${t.coupon_count} emailed`} />
        <MiniStat label="Coupons redeemed" value={`\u20ac${t.coupon_redeemed.toFixed(2)}`} sub={`\u20ac${t.coupon_remaining.toFixed(2)} remaining`} />
      </div>
      {data.by_event && data.by_event.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="on-attend-credits-by-event">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-100">
                <th className="py-1.5">Event</th>
                <th className="text-right">Wallet</th>
                <th className="text-right">Coupon issued</th>
                <th className="text-right">Coupon remaining</th>
                <th className="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.by_event.map(r => (
                <tr key={r.event_id || "_"} className="border-b border-slate-50">
                  <td className="py-1.5 font-medium text-slate-700">{r.event_name}</td>
                  <td className="text-right">{"\u20ac"}{r.wallet_amount.toFixed(2)} <span className="text-slate-400">({r.wallet_count})</span></td>
                  <td className="text-right">{"\u20ac"}{r.coupon_amount.toFixed(2)} <span className="text-slate-400">({r.coupon_count})</span></td>
                  <td className="text-right text-amber-600">{"\u20ac"}{r.coupon_remaining.toFixed(2)}</td>
                  <td className="text-right font-bold text-[#1e3a5f]">{"\u20ac"}{r.total_issued.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function MiniStat({ label, value, sub }) {
  return (
    <div className="bg-slate-50 rounded-xl p-2.5">
      <p className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</p>
      <p className="text-lg font-bold text-[#1e3a5f]">{value}</p>
      {sub && <p className="text-[10px] text-slate-400">{sub}</p>}
    </div>
  );
}


function PieYears({ years, value, onChange, testid }) {
  const opts = [...years, "all"];
  return (
    <div className="flex gap-1 flex-wrap mb-2" data-testid={`${testid}-years`}>
      {opts.map(y => (
        <button key={y} onClick={() => onChange(y)} data-testid={`${testid}-year-${y}`}
          className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium transition-colors ${value === y ? "bg-[#1e3a5f] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
          {y === "all" ? "All years" : y}
        </button>
      ))}
    </div>
  );
}

function PieCard({ title, showTotal, data: allData, byYear = {}, offset, pie, testid }) {
  const thisYear = String(new Date().getFullYear());
  const years = [...new Set([thisYear, ...Object.keys(byYear)])].sort().reverse();
  const [year, setYear] = useState(thisYear);
  const [selected, setSelected] = useState(null);
  const data = year === "all" ? allData : (byYear[year] || []);
  const sum = data.reduce((a, d) => a + (d.value || 0), 0);
  const total = sum || 1;
  const rows = [...data].sort((a, b) => b.value - a.value);
  const color = (name) => COLORS[(data.findIndex(d => d.name === name) + offset) % COLORS.length];
  const toggle = (name) => setSelected(cur => (cur === name ? null : name));
  return (
    <Card title={showTotal ? `${title} (\u20ac${sum.toFixed(2)})` : title} className={selected ? "sm:col-span-3" : ""}>
      <PieYears years={years} value={year} onChange={(y) => { setYear(y); setSelected(null); }} testid={testid} />
      {data.length === 0 && <p className="text-xs text-slate-400 py-8 text-center" data-testid={`${testid}-empty`}>Nothing recorded in {year}.</p>}
      {data.length > 0 && <div className="flex items-center gap-3" data-testid={testid}>
        <div className="w-28 h-28 shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={28} outerRadius={52} paddingAngle={1} stroke="none"
                onClick={(d) => toggle(d.name)} className="cursor-pointer">
                {data.map((d) => <Cell key={d.name} fill={color(d.name)} opacity={!selected || selected === d.name ? 1 : 0.35} />)}
              </Pie>
              <Tooltip formatter={(v) => `\u20ac${Number(v).toFixed(2)}`} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <ul className="flex-1 min-w-0 space-y-1 text-xs">
          {rows.map(d => (
            <li key={d.name}>
              <button onClick={() => toggle(d.name)} title={`Show entries for ${d.name}`}
                className={`w-full flex items-start gap-1.5 text-left rounded px-1 -mx-1 transition-colors hover:bg-slate-50 ${selected === d.name ? "bg-slate-100" : ""}`}
                data-testid={`${testid}-legend-item`}>
                <span className="w-2.5 h-2.5 mt-0.5 rounded-sm shrink-0" style={{ background: color(d.name) }} />
                <span className="flex-1 min-w-0 text-slate-600 leading-tight break-words">{d.name}</span>
                <span className="font-semibold text-slate-700 tabular-nums">{Math.round((d.value / total) * 100)}%</span>
              </button>
            </li>
          ))}
        </ul>
      </div>}
      {selected && <PieSliceDetails pie={pie} slice={selected} year={year === "all" ? "" : year} color={color(selected)} onClose={() => setSelected(null)} testid={testid} />}
    </Card>
  );
}
